import * as Common from "@sp-crm/core";
import { Contact, validEmail } from "@sp-crm/core";
import { produce } from "immer";
import * as React from "react";
import { useAutoComplete } from "store/selectors/hooks";
import { parseRecipients } from "../../util/parse-recipients";

interface RecipientsFieldProps {
    onChange: (e: Common.IContact[]) => void;
    recipients: Common.IContact[];
    suggestions?: Common.IContact[];
    suggestionHeader?: string;
    disabled?: boolean;
    label: "To" | "Cc" | "" | "Customer email";
}

interface RecipientsFieldState {
    inputValue: string;
    isDropdownOpen: boolean;
    highlightedIndex: number | null;
    focusedChipIndex: number | null;
}

const initialState: RecipientsFieldState = {
    inputValue: "",
    isDropdownOpen: false,
    highlightedIndex: null,
    focusedChipIndex: null,
};

type RecipientsFieldAction =
    | { type: "inputChanged"; value: string; hasSuggestions: boolean }
    | { type: "inputCleared" }
    | { type: "dropdownOpened" }
    | { type: "dropdownClosed" }
    | { type: "highlightSet"; index: number }
    | { type: "highlightNext"; maxIndex: number }
    | { type: "highlightPrevious"; maxIndex: number }
    | { type: "highlightReset" }
    | { type: "focusChip"; index: number }
    | { type: "focusNextChip"; maxIndex: number }
    | { type: "focusPreviousChip" }
    | { type: "clearChipFocus" }
    | { type: "inputFocused"; hasValue: boolean; hasSuggestions: boolean }
    | { type: "escapePressed" }
    | { type: "contactAdded" }
    | { type: "pasteCompleted"; unparsed: string };

const reducer: React.Reducer<RecipientsFieldState, RecipientsFieldAction> = (
    state,
    action,
): RecipientsFieldState => {
    return produce(state, draft => {
        switch (action.type) {
            case "inputChanged":
                draft.inputValue = action.value;
                draft.isDropdownOpen =
                    action.value.trim().length > 0 || action.hasSuggestions;
                draft.highlightedIndex = null;
                draft.focusedChipIndex = null;
                break;
            case "inputCleared":
                draft.inputValue = "";
                break;
            case "dropdownOpened":
                draft.isDropdownOpen = true;
                break;
            case "dropdownClosed":
                draft.isDropdownOpen = false;
                draft.highlightedIndex = null;
                break;
            case "highlightSet":
                draft.highlightedIndex = action.index;
                break;
            case "highlightNext":
                draft.highlightedIndex =
                    state.highlightedIndex === null
                        ? 0
                        : Math.min(state.highlightedIndex + 1, action.maxIndex);
                break;
            case "highlightPrevious":
                draft.highlightedIndex =
                    state.highlightedIndex === null
                        ? action.maxIndex
                        : Math.max(state.highlightedIndex - 1, 0);
                break;
            case "highlightReset":
                draft.highlightedIndex = null;
                break;
            case "focusChip":
                draft.focusedChipIndex = action.index;
                break;
            case "focusNextChip":
                if (state.focusedChipIndex === null) {
                    draft.focusedChipIndex = null;
                } else if (state.focusedChipIndex < action.maxIndex) {
                    draft.focusedChipIndex = state.focusedChipIndex + 1;
                } else {
                    draft.focusedChipIndex = null;
                }
                break;
            case "focusPreviousChip":
                if (state.focusedChipIndex === null) {
                    draft.focusedChipIndex = null;
                } else {
                    draft.focusedChipIndex = Math.max(0, state.focusedChipIndex - 1);
                }
                break;
            case "clearChipFocus":
                draft.focusedChipIndex = null;
                break;
            case "inputFocused":
                draft.focusedChipIndex = null;
                if (action.hasValue || action.hasSuggestions) {
                    draft.isDropdownOpen = true;
                }
                break;
            case "escapePressed":
                draft.isDropdownOpen = false;
                draft.highlightedIndex = null;
                break;
            case "contactAdded":
                draft.inputValue = "";
                draft.highlightedIndex = null;
                draft.isDropdownOpen = false;
                break;
            case "pasteCompleted":
                draft.inputValue = action.unparsed;
                draft.highlightedIndex = null;
                draft.isDropdownOpen = false;
                break;
        }
    });
};

const getLabelStyling = (
    label: RecipientsFieldProps["label"],
): { labelWidthClass: string; inputPaddingClass: string } => {
    switch (label) {
        case "To":
        case "Cc":
            return { labelWidthClass: "w-6", inputPaddingClass: "pl-10" };
        case "Customer email":
            return { labelWidthClass: "w-28", inputPaddingClass: "pl-32" };
        case "":
            return { labelWidthClass: "", inputPaddingClass: "" };
        default: {
            const exhaustiveCheck: never = label;
            throw new Error(`Unhandled label: ${exhaustiveCheck}`);
        }
    }
};

export const RecipientsField: React.FC<RecipientsFieldProps> = props => {
    const { recipients, onChange, label, disabled } = props;
    const suggestions = React.useMemo(() => props.suggestions || [], [props.suggestions]);
    const autoComplete = useAutoComplete();
    const [state, dispatch] = React.useReducer(reducer, initialState);

    const { labelWidthClass, inputPaddingClass } = getLabelStyling(label);

    const inputRef = React.useRef<HTMLInputElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);

    const uniqueRecipients = React.useMemo(() => {
        return (recipients || [])
            .filter(x => !!x.email1)
            .reduce((acc, cur) => {
                if (
                    !acc.find(
                        x =>
                            (x.email1 ?? "").toLowerCase() ===
                            (cur.email1 ?? "").toLowerCase(),
                    )
                ) {
                    acc.push(cur);
                }
                return acc;
            }, [] as Common.IContact[]);
    }, [recipients]);

    const filteredSuggestions = React.useMemo(() => {
        if (!state.inputValue.trim()) {
            return suggestions;
        }
        const searchLower = state.inputValue.toLowerCase();
        return suggestions.filter(s => {
            const email = (s.email1 || "").toLowerCase();
            const name = (s.name || "").toLowerCase();
            return email.includes(searchLower) || name.includes(searchLower);
        });
    }, [state.inputValue, suggestions]);

    const availableSuggestions = React.useMemo(() => {
        return filteredSuggestions.filter(
            s =>
                !uniqueRecipients.find(
                    r =>
                        (r.email1 ?? "").toLowerCase() === (s.email1 ?? "").toLowerCase(),
                ),
        );
    }, [filteredSuggestions, uniqueRecipients]);

    const handleAddContact = React.useCallback(
        (email: string) => {
            const trimmed = email.trim();
            if (!trimmed) return;

            const existingContact = [...suggestions, ...recipients].find(
                c => c.email1?.toLowerCase() === trimmed.toLowerCase(),
            );

            if (existingContact) {
                const isDuplicate = uniqueRecipients.find(
                    r => (r.email1 ?? "").toLowerCase() === trimmed.toLowerCase(),
                );
                if (!isDuplicate) {
                    onChange([...recipients, existingContact]);
                }
                dispatch({ type: "contactAdded" });
            } else if (validEmail(trimmed)) {
                onChange([...recipients, Contact.load({ email1: trimmed })]);
                dispatch({ type: "contactAdded" });
            }
        },
        [recipients, suggestions, uniqueRecipients, onChange],
    );

    const handleRemoveChip = React.useCallback(
        (index: number) => {
            const newRecipients = [...recipients];
            newRecipients.splice(index, 1);
            onChange(newRecipients);
            dispatch({ type: "clearChipFocus" });
            inputRef.current?.focus();
        },
        [recipients, onChange],
    );

    const handleInputChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            dispatch({
                type: "inputChanged",
                value: e.target.value,
                hasSuggestions: availableSuggestions.length > 0,
            });
        },
        [availableSuggestions.length],
    );

    const handleKeyDown = React.useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            const isValidEmailInput =
                state.inputValue.trim() && validEmail(state.inputValue.trim());
            const totalDropdownItems =
                availableSuggestions.length + (isValidEmailInput ? 1 : 0);

            if (e.key === "," || e.key === ";") {
                e.preventDefault();
                if (validEmail(state.inputValue.trim())) {
                    handleAddContact(state.inputValue);
                }
            } else if (e.key === "Enter") {
                e.preventDefault();
                if (state.isDropdownOpen && state.highlightedIndex !== null) {
                    if (state.highlightedIndex < availableSuggestions.length) {
                        const suggestion = availableSuggestions[state.highlightedIndex];
                        onChange([...recipients, suggestion]);
                        dispatch({ type: "contactAdded" });
                    } else {
                        handleAddContact(state.inputValue);
                    }
                } else {
                    handleAddContact(state.inputValue);
                }
            } else if (e.key === "Tab") {
                if (state.isDropdownOpen && state.highlightedIndex !== null) {
                    e.preventDefault();
                    if (state.highlightedIndex < availableSuggestions.length) {
                        const suggestion = availableSuggestions[state.highlightedIndex];
                        onChange([...recipients, suggestion]);
                        dispatch({ type: "contactAdded" });
                    } else {
                        handleAddContact(state.inputValue);
                    }
                } else if (
                    state.inputValue.trim() &&
                    validEmail(state.inputValue.trim())
                ) {
                    e.preventDefault();
                    handleAddContact(state.inputValue);
                }
            } else if (e.key === "Backspace") {
                if (state.focusedChipIndex !== null) {
                    e.preventDefault();
                    handleRemoveChip(state.focusedChipIndex);
                } else {
                    const cursorPosition = e.currentTarget.selectionStart;
                    const selectionEnd = e.currentTarget.selectionEnd;
                    const hasSelection = cursorPosition !== selectionEnd;

                    if (
                        !hasSelection &&
                        cursorPosition === 0 &&
                        uniqueRecipients.length > 0
                    ) {
                        e.preventDefault();
                        handleRemoveChip(uniqueRecipients.length - 1);
                    }
                }
            } else if (e.key === "Escape") {
                dispatch({ type: "escapePressed" });
            } else if (e.key === "ArrowDown") {
                if (state.isDropdownOpen && totalDropdownItems > 0) {
                    e.preventDefault();
                    dispatch({ type: "highlightNext", maxIndex: totalDropdownItems - 1 });
                }
            } else if (e.key === "ArrowUp") {
                if (state.isDropdownOpen && totalDropdownItems > 0) {
                    e.preventDefault();
                    dispatch({
                        type: "highlightPrevious",
                        maxIndex: totalDropdownItems - 1,
                    });
                }
            } else if (e.key === "ArrowLeft") {
                if (
                    !state.inputValue &&
                    !state.isDropdownOpen &&
                    uniqueRecipients.length > 0
                ) {
                    e.preventDefault();
                    if (state.focusedChipIndex === null) {
                        dispatch({
                            type: "focusChip",
                            index: uniqueRecipients.length - 1,
                        });
                    } else {
                        dispatch({ type: "focusPreviousChip" });
                    }
                }
            } else if (e.key === "ArrowRight") {
                if (
                    !state.inputValue &&
                    !state.isDropdownOpen &&
                    state.focusedChipIndex !== null
                ) {
                    e.preventDefault();
                    dispatch({
                        type: "focusNextChip",
                        maxIndex: uniqueRecipients.length - 1,
                    });
                }
            } else if (e.key === "Delete" && state.focusedChipIndex !== null) {
                e.preventDefault();
                handleRemoveChip(state.focusedChipIndex);
            }
        },
        [
            state,
            availableSuggestions,
            uniqueRecipients,
            handleAddContact,
            handleRemoveChip,
            recipients,
            onChange,
        ],
    );

    const handleSuggestionClick = React.useCallback(
        (suggestion: Common.IContact) => {
            onChange([...recipients, suggestion]);
            dispatch({ type: "contactAdded" });
            inputRef.current?.focus();
        },
        [recipients, onChange],
    );

    const handlePaste = React.useCallback(
        (e: React.ClipboardEvent<HTMLInputElement>) => {
            const pastedText = e.clipboardData.getData("text");

            const { contacts: parsedContacts, unparsed } = parseRecipients(pastedText);

            if (parsedContacts.length === 0 && !unparsed) return;

            e.preventDefault();

            const newContacts: Common.IContact[] = [];

            parsedContacts.forEach(({ name, email }) => {
                const existingContact = [...suggestions, ...recipients].find(
                    c => c.email1?.toLowerCase() === email.toLowerCase(),
                );

                const isDuplicate = [...uniqueRecipients, ...newContacts].find(
                    r => (r.email1 ?? "").toLowerCase() === email.toLowerCase(),
                );

                if (!isDuplicate) {
                    if (existingContact) {
                        newContacts.push(existingContact);
                    } else {
                        newContacts.push(
                            Contact.load({
                                email1: email,
                                name: name || undefined,
                            }),
                        );
                    }
                }
            });

            if (newContacts.length > 0) {
                onChange([...recipients, ...newContacts]);
            }

            dispatch({ type: "pasteCompleted", unparsed });
        },
        [suggestions, recipients, uniqueRecipients, onChange],
    );

    const handleBlur = React.useCallback(() => {
        if (state.inputValue.trim() && validEmail(state.inputValue.trim())) {
            handleAddContact(state.inputValue);
        }
    }, [state.inputValue, handleAddContact]);

    React.useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target as Node)
            ) {
                dispatch({ type: "dropdownClosed" });
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div ref={containerRef} className="relative flex items-center">
            {label && (
                <label
                    className={`absolute left-2 top-3 block text-sm font-medium text-gray-800 mb-1 ${labelWidthClass}`}>
                    {label}
                </label>
            )}

            <div
                className={`flex-1 flex flex-wrap gap-1 p-2 border border-gray-300 rounded-md bg-white min-h-[42px] ${inputPaddingClass} ${
                    disabled ? "bg-gray-100 cursor-not-allowed" : ""
                }`}>
                {uniqueRecipients.map((recipient, index) => {
                    const displayText =
                        recipient.name && recipient.name !== recipient.email1
                            ? `${recipient.name} <${recipient.email1}>`
                            : recipient.email1;
                    const isFocused = state.focusedChipIndex === index;

                    return (
                        <div
                            key={`${recipient.email1}-${index}`}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-sm ${
                                isFocused
                                    ? "bg-blue-600 text-white"
                                    : "bg-blue-50 text-gray-800"
                            }`}
                            role="button"
                            tabIndex={-1}
                            aria-label={`Recipient: ${displayText}`}>
                            <span>{displayText}</span>
                            <button
                                type="button"
                                onClick={() => handleRemoveChip(index)}
                                disabled={disabled}
                                className={`ml-1 hover:text-red-600 focus:outline-none ${
                                    isFocused ? "text-white hover:text-red-300" : ""
                                }`}
                                aria-label={`Remove ${displayText}`}>
                                ×
                            </button>
                        </div>
                    );
                })}

                <input
                    ref={inputRef}
                    type="text"
                    value={state.inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    onBlur={handleBlur}
                    onFocus={() => {
                        dispatch({
                            type: "inputFocused",
                            hasValue: !!state.inputValue,
                            hasSuggestions: availableSuggestions.length > 0,
                        });
                    }}
                    disabled={disabled}
                    className="flex-1 min-w-[200px] outline-none bg-transparent"
                    autoComplete={autoComplete}
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-bwignore="true"
                    aria-label="Email input"
                    aria-autocomplete="list"
                    aria-controls="recipients-dropdown"
                    aria-expanded={state.isDropdownOpen}
                />
            </div>

            {state.isDropdownOpen &&
                (availableSuggestions.length > 0 ||
                    (state.inputValue.trim() && validEmail(state.inputValue.trim()))) && (
                    <div
                        id="recipients-dropdown"
                        role="listbox"
                        className="absolute top-full z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {availableSuggestions.map((suggestion, index) => {
                            const displayText = suggestion.name
                                ? `${suggestion.name} <${suggestion.email1}>`
                                : suggestion.email1;
                            const isHighlighted = state.highlightedIndex === index;

                            return (
                                <div
                                    key={`${suggestion.email1}-${index}`}
                                    role="option"
                                    aria-selected={isHighlighted}
                                    className={`px-3 py-2 cursor-pointer ${
                                        isHighlighted
                                            ? "bg-blue-100"
                                            : "hover:bg-gray-100"
                                    }`}
                                    onClick={() => handleSuggestionClick(suggestion)}
                                    onMouseEnter={() =>
                                        dispatch({ type: "highlightSet", index })
                                    }>
                                    {displayText}
                                </div>
                            );
                        })}
                        {state.inputValue.trim() && validEmail(state.inputValue.trim()) && (
                            <div
                                role="option"
                                aria-selected={
                                    state.highlightedIndex === availableSuggestions.length
                                }
                                className={`px-3 py-2 cursor-pointer ${
                                    state.highlightedIndex === availableSuggestions.length
                                        ? "bg-blue-100"
                                        : "hover:bg-gray-100"
                                }`}
                                onClick={() => handleAddContact(state.inputValue)}
                                onMouseEnter={() =>
                                    dispatch({
                                        type: "highlightSet",
                                        index: availableSuggestions.length,
                                    })
                                }>
                                {state.inputValue.trim()}
                            </div>
                        )}
                    </div>
                )}
        </div>
    );
};
